// src/shared/stores/usePanelsStore.ts
import { create } from 'zustand';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/config';
import { getAndSanitize } from '../../shared/utils/sanitization';

interface Panel {
  id: string;
  name?: string;
  isActive?: boolean;
  users?: Array<{ userId: string }>;
}

interface PanelsState {
  panels: Panel[];
  isLoading: boolean;
  error: Error | null;
  lastFetched: number | null;
  fetchPanels: (forceRefresh?: boolean) => Promise<Panel[]>;
  setPanels: (panels: Panel[]) => void;
  clearPanels: () => void;
}

// 5 minutes cache time
const CACHE_TIME = 5 * 60 * 1000;

// Track ongoing requests
let currentPanelsFetch: Promise<Panel[]> | null = null;

export const usePanelsStore = create<PanelsState>((set, get) => ({
  panels: [],
  isLoading: false,
  error: null,
  lastFetched: null,

  fetchPanels: async (forceRefresh = false) => {
    const { panels, lastFetched } = get();
    const currentTime = Date.now();
    
    // Return cached data if fresh
    if (
      panels.length > 0 && 
      lastFetched && 
      currentTime - lastFetched < CACHE_TIME && 
      !forceRefresh
    ) {
      return panels;
    }
    
    // Reuse in-flight request if exists
    if (currentPanelsFetch) {
      return currentPanelsFetch;
    }
    
    set({ isLoading: true, error: null });
    
    try {
      // Store promise to deduplicate concurrent requests
      currentPanelsFetch = new Promise(async (resolve, reject) => {
        try {
          const token = getAndSanitize('token');
          if (!token) throw new Error("No token found");
          
          // Get current admin ID from token
          const tokenData = JSON.parse(atob(token.split(".")[1]));
          const adminId = tokenData.userId;
          
          const response = await axios.get(`${API_BASE_URL}/api/auth/panels`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (!response.data?.data || !Array.isArray(response.data.data)) {
            console.warn('[PanelsStore] No panels data received');
            set({ panels: [], isLoading: false, lastFetched: Date.now() });
            resolve([]);
            return;
          }
          
          // Filter panels for current admin
          const allPanels = response.data.data;
          const adminPanels = allPanels.filter(panel => {
            return panel.users && Array.isArray(panel.users) && 
                  panel.users.some(user => user.userId === adminId);
          });
          
          
          set({ 
            panels: adminPanels,
            isLoading: false, 
            lastFetched: Date.now() 
          });
          
          resolve(adminPanels);
        } catch (error) {
          const typedError = error as Error;
          set({ isLoading: false, error: typedError });
          reject(typedError);
        } finally {
          // Clean up tracked request
          currentPanelsFetch = null;
        }
      });
      
      return await currentPanelsFetch;
    } catch (error) {
      console.error('[PanelsStore] Error fetching panels:', error);
      return [];
    }
  },
  
  setPanels: (panels) => set({ panels, lastFetched: Date.now() }),
  
  clearPanels: () => set({ panels: [], lastFetched: null }),
}));